import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import { useDismiss, useFocusTrap } from '../motion';
import Icon from './Icon';
import NavIcon from './NavIcon';
import DateField from './DateField';
import { carryInLessons } from '../lessons';
import RichTextEditor from './RichTextEditor';
import { EMPTY_RICH_DOC, richHasText, type RichDoc } from '../rich-text';
import { useInvalidDates } from './useInvalidDates';
import {
  keyFromName,
  projectProblem,
  tidyKey,
  PROJECT_PRIORITIES,
  PROJECT_TYPE_GROUPS,
  type ProjectDraft,
  type ProjectPriority,
  type Stakeholder,
} from '../projects';
import { seedPhases, type PhaseDraft } from '../phases';

/**
 * STARTING A PROJECT, THE WAY A PROJECT IS ACTUALLY DEFINED — #387.
 *
 * This replaces the two-field "start a board" modal. Every board is a project,
 * so making one is where the project gets defined: what it is, when it runs,
 * how urgent it is, the kind of work, then its phases, its sign-offs and team,
 * and — only if wanted — its charter.
 *
 * ONE THING IS STILL REQUIRED, AND IT IS THE NAME. Everything else has a
 * sensible default or is optional, and **Start blank** makes a named, empty
 * project in one click for somebody who wants to shape it later. Asking for
 * everything up front is how a project never gets created; offering it, on
 * steps that can be walked past, is how the record ends up complete.
 *
 * THE STEPS. Name it writes the whole project definition through `createProject`;
 * Phases seed from the type and lay out across the dates; Charter captures the
 * governance metadata and offers to start the charter document. Team and
 * sign-off setup are NOT a wizard step — they live in Settings and People &
 * access, where they are edited over a project's life rather than guessed at
 * birth, so there is no half-built "coming soon" step to walk through here.
 */

const STEPS: { n: number; label: string; optional?: boolean }[] = [
  { n: 1, label: 'Name it' },
  { n: 2, label: 'Phases' },
  { n: 3, label: 'Charter', optional: true },
];

/** The pill tint per priority — the same colour language a card uses. */
const PRIO_CLASS: Record<ProjectPriority, string> = {
  critical: 'p-crit',
  high: 'p-high',
  medium: 'p-med',
  low: 'p-low',
};

/**
 * A date input hands back `yyyy-mm-dd`; the app keeps time as milliseconds. Read
 * as LOCAL midnight, not `Date.parse`'s UTC, so a date never shows as the day
 * before in a western timezone.
 */
function dayToMs(s: string): number | undefined {
  if (!s) return undefined;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d).getTime();
}

export default function NewProjectWizard({ close }: { close: () => void }) {
  const { leaving, dismiss } = useDismiss(close);
  const createProject = useApp((s) => s.createProject);
  const setDeliveryMode = useApp((s) => s.setDeliveryMode);
  const draftDocument = useApp((s) => s.draftDocument);
  const projects = useApp((s) => s.projects);
  const lessons = useApp((s) => s.lessons);
  const setView = useApp((s) => s.setView);

  const [at, setAt] = useState(1);
  // Hold the wizard's buttons while any date box on the current step holds an
  // entry it could not read (see useInvalidDates).
  const badDates = useInvalidDates();

  // ── Step 1: the definition ────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  // Once the key is theirs, the suggestion stops following the name — otherwise
  // a deliberate choice is overwritten by the next keystroke in the name box.
  const [keyIsTheirs, setKeyIsTheirs] = useState(false);
  const [description, setDescription] = useState<RichDoc>(EMPTY_RICH_DOC);
  // Success measures — what good looks like. The same field the About block
  // carries (successMeasure); captured here so a project can be born with it,
  // not only have it added later. createProject already persists it.
  const [successMeasure, setSuccessMeasure] = useState<RichDoc>(EMPTY_RICH_DOC);
  const [startDate, setStartDate] = useState('');
  const [goLive, setGoLive] = useState('');
  const [priority, setPriority] = useState<ProjectPriority | ''>('');
  const [projectType, setProjectType] = useState('');
  // Whether the type picker's menu is open — so Escape backs out of the menu
  // first, and only closes the whole wizard once nothing nested is open.
  const [typeOpen, setTypeOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef);

  // ── Step 2: the phases ─────────────────────────────────────────────────────
  // Seeded from the type and the dates, then the person's to reshape. Once they
  // touch the list, the type no longer overwrites it — otherwise going back to
  // step 1 to fix a typo in the type would throw away their phase edits.
  const [phases, setPhases] = useState<PhaseDraft[]>([]);
  const [phasesTouched, setPhasesTouched] = useState(false);

  // ── Step 4: the charter ────────────────────────────────────────────────────
  // The governance METADATA the About block keeps (0100/0101), captured at birth
  // so a project starts defined. The charter NARRATIVE is a document (0102), not
  // typed here; `startCharterDoc` (on by default) drafts that document once the
  // project exists, seeded with the template, so the PM lands with one to write.
  const [projectManager, setProjectManager] = useState('');
  const [sponsor, setSponsor] = useState('');
  const [vendor, setVendor] = useState('');
  const [vendorContact, setVendorContact] = useState('');
  const [contractRef, setContractRef] = useState('');
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [startCharterDoc, setStartCharterDoc] = useState(true);

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const suggestedKey = keyIsTheirs ? key : keyFromName(name);
  const last = at >= STEPS.length;

  /*
   * The seed follows the type and the dates until the person edits it. Spreading
   * the target days across the start → go-live span here means a project created
   * with dates has a real schedule the moment it exists — what #376 and #384 read.
   */
  useEffect(() => {
    if (phasesTouched) return;
    setPhases(seedPhases(projectType, dayToMs(startDate), dayToMs(goLive)));
  }, [projectType, startDate, goLive, phasesTouched]);

  /*
   * Escape closes the wizard, from anywhere in it — a modal should always answer
   * Escape, and this one never did (there was no handler at all). A global
   * listener catches it even when focus is on a bare button or nothing, the way
   * the card modal does. It backs out one step at a time: while the type picker's
   * menu is open, that menu's own Escape closes it first, so this steps aside.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || typeOpen) return;
      dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [typeOpen, dismiss]);

  const draft = (): Omit<ProjectDraft, 'name' | 'key'> => ({
    description: richHasText(description) ? description : undefined,
    successMeasure: richHasText(successMeasure) ? successMeasure : undefined,
    startDate: dayToMs(startDate),
    goLive: dayToMs(goLive),
    priority: priority || undefined,
    projectType: projectType || undefined,
    phases,
    // Charter step (metadata). Empty strings are omitted so a project made
    // without them carries nothing, exactly as the About block reads.
    projectManager: projectManager.trim() || undefined,
    sponsor: sponsor.trim() || undefined,
    vendor: vendor.trim() || undefined,
    vendorContact: vendorContact.trim() || undefined,
    contractRef: contractRef.trim() || undefined,
    stakeholders: stakeholders
      .map((s) => ({ name: s.name.trim(), role: s.role?.trim() || undefined }))
      .filter((s) => s.name),
  });

  /** Both Finish and Start blank land here; blank writes only name and key. */
  const create = async (blank: boolean) => {
    // A date box on this step holds something unreadable — read it from the ref,
    // since clicking a footer button blurs and flags the box in the same tick.
    if (badDates.ref.current) return;
    const wrong = projectProblem({ name, key: suggestedKey }, projects);
    if (wrong) {
      setProblem(wrong);
      setAt(1);
      return;
    }
    setBusy(true);
    setProblem('');
    try {
      await createProject(name, suggestedKey, blank ? undefined : draft());
      // A board made through this wizard is a project — set it explicitly on the
      // new board (createProject has already switched to it), so its delivery mode
      // is a recorded choice rather than left to the absent-means-project default.
      await setDeliveryMode('project');
      // The board underneath has already switched to the new project, so the
      // charter is drafted onto it. A defined project starts with a charter
      // unless it was unticked; a blank project (name + key only) gets none.
      // Best-effort: the project is made either way, and a charter can be
      // started later from the Plan, so a draft failure never blocks finishing.
      if (!blank && startCharterDoc) {
        await draftDocument('charter', 'Project charter');
      }
      // Land on the new project's Plan, not the empty Board. createProject has
      // already switched the board underneath, so Plan is the new project's —
      // its phases, its About, and the "Start a charter" on-ramp. Finishing the
      // wizard used to drop onto blank columns with no sign anything happened and
      // nowhere obvious to go next; the Plan is where the project actually begins.
      setView('plan');
      dismiss();
    } catch (e) {
      const why = e instanceof Error ? e.message : String(e);
      setProblem(
        /permission/i.test(why)
          ? 'Your account is not allowed to start a project here. Ask an administrator.'
          : `It could not be created — ${why}`
      );
      setBusy(false);
    }
  };

  const next = () => {
    // A date box on this step holds something unreadable — hold Next until it is
    // fixed or cleared, so a bad date can't be carried past the step it is on.
    if (badDates.ref.current) return;
    // The name and key are the one gate; the later steps are all optional, so
    // this is the only place Next can refuse.
    if (at === 1) {
      const wrong = projectProblem({ name, key: suggestedKey }, projects);
      if (wrong) return setProblem(wrong);
      setProblem('');
    }
    if (last) void create(false);
    else setAt(at + 1);
  };

  return (
    <div
      className={`overlay setup-overlay ${leaving ? 'is-leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="New project"
    >
      <div ref={panelRef} tabIndex={-1} className={`setup wizard ${leaving ? 'is-leaving' : ''}`}>
        <div className="setup-top">
          <span className="setup-count">
            Step {at} of {STEPS.length}
          </span>
          <button className="modal-close" onClick={dismiss} aria-label="Close">
            <Icon name="close" size={16} />
          </button>
        </div>

        <ol className="wiz-steps" aria-hidden="true">
          {STEPS.map((s) => (
            <li
              key={s.n}
              className={`wiz-step ${s.n === at ? 'current' : s.n < at ? 'done' : 'todo'}`}
            >
              <span className="wiz-num">{s.n < at ? <Icon name="check" size={14} /> : s.n}</span>
              <span className="wiz-lab">
                {s.label}
                {s.optional && <span className="wiz-opt">optional</span>}
              </span>
            </li>
          ))}
        </ol>

        <div className="setup-body">
          {/* Called as functions, not mounted as <StepName/>. These bodies close
              over the wizard's state and are defined inside this component, so
              mounting them would give each render a fresh component identity —
              React would remount the whole step on every keystroke, destroying
              the focused input and re-firing the name field's autoFocus, which
              threw the caret back to Project name after one character. Calling
              them inlines their elements, so each input is kept across renders. */}
          {at === 1 ? StepName() : at === 2 ? StepPhases() : StepCharter()}
        </div>

        {problem && (
          <p className="feedback-problem" role="alert">
            {problem}
          </p>
        )}

        <div className="setup-foot">
          {at > 1 ? (
            <button className="btn" onClick={() => setAt(at - 1)} disabled={busy}>
              Back
            </button>
          ) : (
            <button className="btn" onClick={() => void create(true)} disabled={busy || badDates.any}>
              Start blank
            </button>
          )}
          <span className="setup-spacer" />
          {/* Once the name is behind you (any step past the first), the project
              can be created here and now — the steps ahead are optional and must
              never be a turnstile you have to click through to finish. On the
              last step there is only this, as the primary. */}
          {at > 1 && !last && (
            <button className="btn" onClick={() => void create(false)} disabled={busy || badDates.any}>
              Create project
            </button>
          )}
          <button className="btn btn-primary" onClick={next} disabled={busy || badDates.any}>
            {busy ? 'Creating…' : last ? 'Create project' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Step 1 body ────────────────────────────────────────────────────────────
  function StepName() {
    return (
      <>
        <p className="wiz-lead">
          What it&rsquo;s called, what it&rsquo;s for, when it runs, and the kind of project it is.
        </p>
        <div className="wiz-row2">
          <label className="stack">
            <span>
              Project name <span className="req" aria-hidden="true">*</span>
            </span>
            <input
              className="input"
              autoFocus
              aria-required="true"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Records digitisation"
            />
          </label>
          <label className="stack">
            <span>
              Code <span className="req" aria-hidden="true">*</span>
            </span>
            <input
              className="input key-input"
              aria-required="true"
              value={suggestedKey}
              onChange={(e) => {
                setKeyIsTheirs(true);
                setKey(tidyKey(e.target.value));
              }}
              placeholder="DGT"
            />
          </label>
        </div>
        <span className="hint wiz-keyhint">
          {suggestedKey
            ? `Issues will be numbered ${suggestedKey}#1, ${suggestedKey}#2, and so on.`
            : 'A short key goes in front of every issue number.'}
        </span>

        {/* div, not label: a rich-text field is a toolbar of buttons plus an
            editable surface, and a <label> forwards a click in the field to its
            first labelable descendant (the H₂ button), which turned the first line
            into a heading on click. The editor carries its own aria-label. */}
        <div className="stack">
          <span>Description</span>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            ariaLabel="Description"
            placeholder="What this project sets out to do."
          />
        </div>

        {/* Success measures — the pair to the description, exactly as the About
            block orders them (0108/#387). Captured at birth so "what good looks
            like" is on the record from the start, not a field discovered later. */}
        <div className="stack">
          <span>Success measures / expected outcomes</span>
          <RichTextEditor
            value={successMeasure}
            onChange={setSuccessMeasure}
            ariaLabel="Success measures / expected outcomes"
            placeholder="What good looks like — the outcome this delivers, in terms a sponsor would recognise."
          />
        </div>

        <div className="wiz-row2e">
          <label className="stack">
            <span>Start date</span>
            <DateField
              ariaLabel="Start date"
              value={startDate}
              max={goLive || undefined}
              onChange={setStartDate}
              onInvalidChange={badDates.mark('start')}
              variant="form"
            />
          </label>
          <label className="stack">
            <span>Target Go-Live</span>
            <DateField
              ariaLabel="Target Go-Live"
              value={goLive}
              min={startDate || undefined}
              onChange={setGoLive}
              onInvalidChange={badDates.mark('golive')}
              variant="form"
            />
          </label>
        </div>

        <div className="stack">
          <span>Priority</span>
          <div className="prio">
            {PROJECT_PRIORITIES.map((p) => (
              <button
                key={p.value}
                type="button"
                className={`prio-p ${PRIO_CLASS[p.value]} ${priority === p.value ? 'on' : ''}`}
                aria-pressed={priority === p.value}
                onClick={() => setPriority(priority === p.value ? '' : p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="stack">
          <span>
            Project type <span className="hint">— sets the starting phases</span>
          </span>
          <TypePicker value={projectType} onChange={setProjectType} onOpenChange={setTypeOpen} />
          {/* #490 teaser — once a type is chosen, say how many past lessons may
              apply (same-type vs workspace) and that the full read waits on the
              Plan. No reading inside the fast wizard; nothing is seeded. */}
          {projectType.trim() &&
            (() => {
              const typeOf = (boardId: string) => projects.find((p) => p.id === boardId)?.projectType;
              const { sameType, rest, total } = carryInLessons(lessons, { currentType: projectType, typeOf });
              if (total === 0) return null;
              return (
                <div className="ci-teaser">
                  <span className="ci-teaser-ic">
                    <NavIcon view="lessons" size={18} />
                  </span>
                  <span className="ci-teaser-tx">
                    <b>
                      {total} past {total === 1 ? 'lesson' : 'lessons'} may apply
                    </b>{' '}
                    — {sameType.length > 0 ? `${sameType.length} from ${projectType} projects, ` : ''}
                    {rest.length} from across the workspace. You’ll see them on the Plan after you create
                    this project.
                  </span>
                </div>
              );
            })()}
        </div>
      </>
    );
  }

  // ── Step 2 body: the phases ────────────────────────────────────────────────
  function StepPhases() {
    // Every edit marks the list as the person's, so the type stops reseeding it.
    const edit = (next: PhaseDraft[]) => {
      setPhasesTouched(true);
      setPhases(next);
    };
    const rename = (i: number, name: string) =>
      edit(phases.map((p, j) => (j === i ? { ...p, name } : p)));
    const setDate = (i: number, targetEnd: string) =>
      edit(phases.map((p, j) => (j === i ? { ...p, targetEnd: targetEnd || undefined } : p)));
    const move = (i: number, dir: -1 | 1) => {
      const j = i + dir;
      if (j < 0 || j >= phases.length) return;
      const next = [...phases];
      [next[i], next[j]] = [next[j], next[i]];
      edit(next);
    };
    const remove = (i: number) => edit(phases.filter((_, j) => j !== i));
    const add = () => edit([...phases, { name: '' }]);

    return (
      <>
        <p className="wiz-lead">
          The stages this project runs in — seeded from the type, and yours to reshape. Each stage
          starts <em>Planned</em>; set a target day and they lay out across{' '}
          {startDate && goLive ? 'the run' : 'the project once it has dates'}.
        </p>

        <ol className="wiz-phase-list">
          {phases.map((p, i) => (
            <li className="wiz-phase" key={i}>
              <span className="wiz-phase-n">{i + 1}</span>
              <input
                className="input wiz-phase-name"
                value={p.name}
                placeholder="Phase name"
                aria-label={`Phase ${i + 1} name`}
                onChange={(e) => rename(i, e.target.value)}
              />
              <DateField
                ariaLabel={`Phase ${i + 1} target end`}
                value={p.targetEnd ?? ''}
                min={startDate || undefined}
                max={goLive || undefined}
                onChange={(v) => setDate(i, v)}
                onInvalidChange={badDates.mark(`phase${i}`)}
                variant="form"
              />
              <span className="wiz-phase-moves">
                <button
                  type="button"
                  className="btn btn-small"
                  disabled={i === 0}
                  aria-label={`Move phase ${i + 1} up`}
                  onClick={() => move(i, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn btn-small"
                  disabled={i === phases.length - 1}
                  aria-label={`Move phase ${i + 1} down`}
                  onClick={() => move(i, 1)}
                >
                  ↓
                </button>
              </span>
              <button
                type="button"
                className="wiz-phase-x"
                aria-label={`Remove phase ${i + 1}`}
                onClick={() => remove(i)}
              >
                <Icon name="close" size={14} />
              </button>
            </li>
          ))}
        </ol>

        <button type="button" className="btn" onClick={add}>
          Add a phase
        </button>

        {phases.length === 0 && (
          <p className="hint wiz-phase-none">
            No phases — the project will start without any. You can add them here or in Settings
            later.
          </p>
        )}
      </>
    );
  }

  // ── Step 3 body: the charter ───────────────────────────────────────────────
  function StepCharter() {
    const setStake = (i: number, patch: Partial<Stakeholder>) =>
      setStakeholders((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    return (
      <>
        <p className="wiz-lead">
          Who runs it, who&rsquo;s delivering it, and its charter. You can start the charter document
          now and write it once the project exists.
        </p>

        <div className="wiz-row2e">
          <label className="stack">
            <span>Project Manager</span>
            <input
              className="input"
              value={projectManager}
              onChange={(e) => setProjectManager(e.target.value)}
              placeholder="Who is accountable for delivery"
            />
          </label>
          <label className="stack">
            <span>Sponsor</span>
            <input
              className="input"
              value={sponsor}
              onChange={(e) => setSponsor(e.target.value)}
              placeholder="Who owns it on the client side"
            />
          </label>
        </div>

        <div className="wiz-row2e">
          <label className="stack">
            <span>Vendor</span>
            <input
              className="input"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="Who is delivering it"
            />
          </label>
          <label className="stack">
            <span>Vendor contact</span>
            <input
              className="input"
              value={vendorContact}
              onChange={(e) => setVendorContact(e.target.value)}
              placeholder="Name or email at the vendor"
            />
          </label>
        </div>

        <label className="stack">
          <span>Contract / statement of work (SOW) reference</span>
          <input
            className="input"
            value={contractRef}
            onChange={(e) => setContractRef(e.target.value)}
            placeholder="Contract or statement-of-work number or link"
          />
        </label>

        <div className="stack">
          <span>Stakeholders</span>
          <ul className="plan2-stake-edit">
            {stakeholders.map((s, i) => (
              <li key={i}>
                <input
                  className="input"
                  value={s.name}
                  onChange={(e) => setStake(i, { name: e.target.value })}
                  placeholder="Name"
                />
                <input
                  className="input"
                  value={s.role ?? ''}
                  onChange={(e) => setStake(i, { role: e.target.value })}
                  placeholder="Role"
                />
                <button
                  type="button"
                  className="plan2-stake-x"
                  aria-label={`Remove ${s.name || 'this stakeholder'}`}
                  onClick={() => setStakeholders((xs) => xs.filter((_, j) => j !== i))}
                >
                  <Icon name="close" size={13} />
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="plan2-stake-add"
            onClick={() => setStakeholders((xs) => [...xs, { name: '', role: '' }])}
          >
            + Add stakeholder
          </button>
        </div>

        <label className="add-toggle">
          <input
            type="checkbox"
            checked={startCharterDoc}
            onChange={(e) => setStartCharterDoc(e.target.checked)}
          />
          <span>
            <b>Start a charter document for this project</b>
            <em>
              Creates a charter (scope, milestones, risk register, RACI and sign-off) seeded with
              the standard template, ready to write and send for sign-off once the project is
              created.
            </em>
          </span>
        </label>
      </>
    );
  }
}

/**
 * A SEARCHABLE, GROUPED TYPE PICKER — never a fixed short list, and never a cage.
 *
 * The catalogue is broad and searchable, and anything typed that is not in it
 * becomes a custom type — because an ICT team implements more kinds of thing
 * than any list can name up front. The type only seeds the starting phases.
 */
function TypePicker({
  value,
  onChange,
  onOpenChange,
}: {
  value: string;
  onChange: (v: string) => void;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  // Tell the wizard when the menu opens or closes, so its Escape handler knows
  // to let this menu's own Escape close first before it closes the wizard.
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return PROJECT_TYPE_GROUPS;
    return PROJECT_TYPE_GROUPS.map((g) => ({
      group: g.group,
      types: g.types.filter((t) => t.toLowerCase().includes(needle)),
    })).filter((g) => g.types.length > 0);
  }, [q]);

  const custom = q.trim();
  const exact = PROJECT_TYPE_GROUPS.some((g) =>
    g.types.some((t) => t.toLowerCase() === custom.toLowerCase())
  );

  const pick = (t: string) => {
    onChange(t);
    setOpen(false);
    setQ('');
  };

  return (
    // Closes when focus leaves the picker (a click or tab anywhere outside) or
    // on Escape — no full-screen scrim, which a scrollable modal clips and
    // stacks badly against.
    <div
      className="wiz-type"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <button
        type="button"
        className="wiz-type-trigger"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={value ? 'wiz-type-val' : 'wiz-type-ph'}>
          {value || 'Choose a type, or name your own'}
        </span>
        <span className={`wiz-chev ${open ? 'up' : ''}`}>
          <Icon name="chevron" size={18} />
        </span>
      </button>

      {open && (
        <div className="wiz-type-menu" role="listbox">
          <input
            className="input wiz-type-search"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search every IT project type…"
          />
          <div className="wiz-type-list">
            {groups.map((g) => (
              <div className="wiz-type-group" key={g.group}>
                <div className="wiz-type-glabel">{g.group}</div>
                {g.types.map((t) => (
                  <button
                    type="button"
                    key={t}
                    className={`wiz-type-opt ${value === t ? 'on' : ''}`}
                    role="option"
                    aria-selected={value === t}
                    // Keep focus on the search input during the click: WebKit does
                    // not focus a button on mousedown, so without this the picker's
                    // onBlur (relatedTarget null) closes the menu before onClick can
                    // run, and the pick is silently lost.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            ))}
            {custom && !exact && (
              <button
                type="button"
                className="wiz-type-opt wiz-type-custom"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(custom)}
              >
                Use &ldquo;{custom}&rdquo; as a custom type
              </button>
            )}
            {groups.length === 0 && !custom && (
              <p className="hint wiz-type-empty">Type to search, or name your own.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
